import React, { useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQScreen: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqs: FAQ[] = [
    {
      id: '1',
      question: 'How do I create an account?',
      answer: 'You can create an account by clicking the Sign Up button and entering your phone number. You will receive an OTP to verify your number, after which you can set up your profile.',
      category: 'account',
    },
    {
      id: '2',
      question: 'How do I book an expert consultation?',
      answer: 'Navigate to the Experts section, browse through available experts, select one that matches your needs, choose a time slot, and confirm your booking. You will receive a confirmation once the expert accepts your booking.',
      category: 'experts',
    },
    {
      id: '3',
      question: 'What payment methods are accepted?',
      answer: 'We accept all major credit/debit cards, UPI payments, net banking, and wallet payments. All transactions are secured with bank-grade encryption.',
      category: 'payments',
    },
    {
      id: '4',
      question: 'How do I cancel a booking?',
      answer: 'You can cancel a booking from the My Bookings section. Please note that cancellations made less than 24 hours before the scheduled session may not be eligible for a full refund.',
      category: 'experts',
    },
    {
      id: '5',
      question: 'Are the experts SEBI registered?',
      answer: 'Yes, all our experts are SEBI registered professionals. You can verify their registration number on the SEBI website.',
      category: 'experts',
    },
    {
      id: '6',
      question: 'How do I add money to my wallet?',
      answer: 'Go to the Wallet section and click on Deposit. Enter the amount you want to add and select your preferred payment method to complete the transaction.',
      category: 'payments',
    },
    {
      id: '7',
      question: 'Is my data secure?',
      answer: 'Yes, we use industry-standard 256-bit encryption to protect your data. We never share your personal information with third parties without your consent.',
      category: 'security',
    },
    {
      id: '8',
      question: 'How do I reset my password?',
      answer: 'Click on Forgot Password on the login screen, enter your registered phone number, and follow the instructions sent to your phone to reset your password.',
      category: 'account',
    },
  ];

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'account', label: 'Account' },
    { id: 'experts', label: 'Experts' },
    { id: 'payments', label: 'Payments' },
    { id: 'security', label: 'Security' },
  ];

  const filteredFaqs = selectedCategory === 'all'
    ? faqs
    : faqs.filter((faq) => faq.category === selectedCategory);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-3xl lg:mx-auto">
        <CHeader title="Frequently Asked Questions" />

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className="px-4 py-2 rounded-full whitespace-nowrap transition-colors"
              style={{
                backgroundColor: selectedCategory === cat.id ? colors.primary : colors.inputBackground,
                color: selectedCategory === cat.id ? colors.white : colors.grayScale500,
              }}
            >
              <CText
                type="M12"
                color={selectedCategory === cat.id ? colors.white : colors.grayScale500}
              >
                {cat.label}
              </CText>
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFaqs.map((faq) => {
            const isExpanded = expandedId === faq.id;

            return (
              <div
                key={faq.id}
                className="rounded-xl border overflow-hidden"
                style={{
                  backgroundColor: colors.inputBackground,
                  borderColor: colors.borderColor,
                }}
              >
                <button
                  onClick={() => toggleExpand(faq.id)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <CText type="M14" color={colors.textColor} style={{ flex: 1, paddingRight: 12 }}>
                    {faq.question}
                  </CText>
                  {React.createElement(
                    isExpanded ? FiChevronUp : FiChevronDown as any,
                    { size: 20, color: colors.grayScale500 }
                  )}
                </button>

                {isExpanded && (
                  <div
                    className="px-4 pb-4 border-t"
                    style={{ borderColor: colors.borderColor }}
                  >
                    <CText
                      type="r14"
                      color={colors.grayScale500}
                      style={{ lineHeight: 1.6, paddingTop: 12 }}
                    >
                      {faq.answer}
                    </CText>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact Support */}
        <div
          className="mt-8 p-4 rounded-xl text-center"
          style={{ backgroundColor: colors.primary + '15' }}
        >
          <CText type="M14" color={colors.textColor} style={{ marginBottom: 4 }}>
            Can't find what you're looking for?
          </CText>
          <CText type="r14" color={colors.primary} style={{ cursor: 'pointer' }}>
            Contact Support →
          </CText>
        </div>
      </div>
    </div>
  );
};

export default FAQScreen;
